/**
 * Tabs over server-rendered panels, following the WAI-ARIA tabs pattern:
 * one tab in the tab order at a time, arrow keys move between tabs, Home and
 * End jump to the ends, and activation follows focus.
 *
 * The tab list ships hidden so that without this script the section reads as
 * a single category; revealing it is the first thing the script does.
 *
 * Switching also rewrites the section heading and its "all products" link to
 * match the chosen category, which is what the design does. A panel the page
 * has not shown yet keeps its grid in a <template>; it goes in the first time
 * its tab is chosen - or a moment before, when a pointer rests on the tab or
 * a press starts on it. Then the cans of the grid's first two rows start
 * loading while the panel is still hidden, so they are there when it shows.
 */
const INTENT_MS = 80;

class SnusifyTabs extends HTMLElement {
  #tabs = [];
  #timer = 0;
  #heading = null;
  #link = null;
  #linkText = null;
  /** Recreated on every connect: a controller aborted by a disconnect
      cannot be reused, and listeners bound to it would silently never fire. */
  #abort = null;

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const list = this.querySelector('[data-tabs-list]');
    if (!list) return;

    this.#tabs = [...list.querySelectorAll('[role="tab"]')];
    this.#heading = this.querySelector('[data-tabs-heading]');
    this.#link = this.querySelector('[data-tabs-link]');
    this.#linkText = this.querySelector('[data-tabs-link-text]');
    if (this.#tabs.length === 0) return;

    list.hidden = false;

    const { signal } = this.#abort;
    for (const [i, tab] of this.#tabs.entries()) {
      tab.addEventListener('click', () => this.select(i), { signal });
      tab.addEventListener(
        'pointerenter',
        (event) => {
          if (event.pointerType !== 'mouse') return;
          clearTimeout(this.#timer);
          this.#timer = window.setTimeout(() => this.#warm(i), INTENT_MS);
        },
        { signal }
      );
      tab.addEventListener('pointerleave', () => clearTimeout(this.#timer), { signal });
      tab.addEventListener('pointerdown', () => this.#warm(i), { signal });
    }

    list.addEventListener(
      'keydown',
      (event) => {
        const current = this.#tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
        const last = this.#tabs.length - 1;
        const targets = { ArrowRight: current + 1, ArrowLeft: current - 1, Home: 0, End: last };
        if (!(event.key in targets)) return;
        event.preventDefault();
        this.select(targets[event.key], { focus: true });
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#abort?.abort();
    clearTimeout(this.#timer);
  }

  /**
   * @param {Element} tab
   * @returns {HTMLElement | null}
   */
  #panelOf(tab) {
    return this.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls') ?? '')}`);
  }

  /** @param {Element} panel */
  #unpack(panel) {
    const cards = panel.querySelector('template[data-tab-cards]');
    cards?.replaceWith(cards.content);
  }

  /**
   * Put a hidden panel's grid in and start loading its first two rows of
   * cans: as many columns as the grid on show has, twice.
   * @param {number} index
   */
  #warm(index) {
    const panel = this.#panelOf(this.#tabs[index]);
    if (!panel?.hidden) return;
    this.#unpack(panel);
    const shown = this.querySelector('.snusify-tabs__panel:not([hidden]) .snusify-product-grid');
    const columns = shown ? getComputedStyle(shown).gridTemplateColumns.split(' ').length : Infinity;
    for (const img of [...panel.querySelectorAll('.snusify-product__art img')].slice(0, columns * 2)) {
      if (img instanceof HTMLImageElement && img.loading === 'lazy') img.loading = 'eager';
    }
  }

  /**
   * @param {number} index - wraps in both directions
   * @param {{ focus?: boolean }} [options]
   */
  select(index, options = {}) {
    const count = this.#tabs.length;
    const next = ((index % count) + count) % count;

    for (const [i, tab] of this.#tabs.entries()) {
      const on = i === next;
      tab.setAttribute('aria-selected', String(on));
      tab.tabIndex = on ? 0 : -1;
      const panel = this.#panelOf(tab);
      if (!panel) continue;
      if (on) this.#unpack(panel);
      panel.hidden = !on;
    }

    const tab = this.#tabs[next];
    if (this.#heading) this.#heading.textContent = tab.dataset.label ?? '';
    if (this.#link && tab.dataset.url) this.#link.href = tab.dataset.url;
    if (this.#linkText) this.#linkText.textContent = tab.dataset.all ?? '';
    if (options.focus) tab.focus();
  }
}

if (!customElements.get('snusify-tabs')) {
  customElements.define('snusify-tabs', SnusifyTabs);
}
